import React from "react";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  "@keyframes starTwinkle": {
    "0%, 100%": { opacity: .48, transform: "scale(.86)" },
    "50%": { opacity: 1, transform: "scale(1.18)" },
  },
  "@keyframes cloudDrift": {
    "0%, 100%": { transform: "translate3d(0,0,0)" },
    "50%": { transform: "translate3d(5px,-1px,0)" },
  },
  "@keyframes sunBreathe": {
    "0%, 100%": { transform: "scale(.96) rotate(0deg)", opacity: .72 },
    "50%": { transform: "scale(1.08) rotate(18deg)", opacity: 1 },
  },
  "@keyframes shootingStar": {
    "0%, 74%": { opacity: 0, transform: "translate3d(0,0,0) rotate(-18deg) scaleX(.45)" },
    "79%": { opacity: .9 },
    "90%, 100%": { opacity: 0, transform: "translate3d(-23px,8px,0) rotate(-18deg) scaleX(1)" },
  },

  button: {
    position: "relative",
    width: 58,
    height: 28,
    flexShrink: 0,
    padding: 0,
    border: 0,
    borderRadius: 999,
    overflow: "hidden",
    cursor: "pointer",
    isolation: "isolate",
    background: "transparent",
    boxShadow:
      "0 0 0 1px var(--border), inset 0 0 0 1px rgba(255,255,255,.07), 0 8px 20px rgba(0,0,0,.11)",
    transition:
      "transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease, filter .22s ease",
    "&:hover": {
      transform: "translateY(-1px) scale(1.025)",
      boxShadow:
        "0 0 0 1px var(--border-accent), inset 0 0 0 1px rgba(255,255,255,.10), 0 12px 25px rgba(0,0,0,.15)",
      filter: "saturate(1.08)",
    },
    "&:active": { transform: "translateY(0) scale(.97)" },
    "&:focus-visible": { outline: "2px solid var(--accent)", outlineOffset: 3 },
  },

  compact: { width: 50, height: 25 },

  sky: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    transition: "background .42s ease, filter .42s ease",
  },
  lightSky: {
    background:
      "radial-gradient(circle at 79% 28%,rgba(255,255,255,.78),transparent 20%),linear-gradient(180deg,#94d9ff 0%,#ccecff 46%,#f7dc80 48%,#95bd72 100%)",
  },
  darkSky: {
    background:
      "radial-gradient(circle at 23% 24%,rgba(82,105,225,.28),transparent 28%),linear-gradient(180deg,#122760 0%,#172753 47%,#1c2748 48%,#25345a 100%)",
  },

  atmosphere: {
    position: "absolute",
    zIndex: 1,
    inset: 0,
    pointerEvents: "none",
    transition: "opacity .35s ease",
  },
  dayAtmosphere: {
    opacity: 1,
    background:
      "linear-gradient(115deg,rgba(255,255,255,.14),transparent 34%),radial-gradient(circle at 82% 36%,rgba(255,223,98,.28),transparent 30%)",
  },
  nightAtmosphere: {
    opacity: 1,
    background:
      "linear-gradient(115deg,rgba(78,109,226,.08),transparent 42%),radial-gradient(circle at 18% 28%,rgba(113,128,255,.16),transparent 28%)",
  },

  ridgeBack: {
    position: "absolute",
    zIndex: 2,
    left: -7,
    right: -7,
    bottom: -2,
    height: "54%",
    opacity: .88,
    clipPath:
      "polygon(0 76%,18% 42%,31% 65%,47% 26%,62% 62%,77% 38%,100% 73%,100% 100%,0 100%)",
    background: "rgba(39,71,96,.75)",
    transition: "background-color .38s ease, transform .38s ease, opacity .38s ease",
  },
  ridgeFront: {
    position: "absolute",
    zIndex: 3,
    left: -8,
    right: -8,
    bottom: -3,
    height: "43%",
    clipPath:
      "polygon(0 82%,19% 54%,36% 74%,51% 39%,68% 75%,83% 51%,100% 81%,100% 100%,0 100%)",
    background: "rgba(26,48,73,.88)",
    transition: "background-color .38s ease, transform .38s ease",
  },
  dayRidgeBack: { background: "rgba(87,122,92,.73)", transform: "translateY(1px)" },
  dayRidgeFront: { background: "rgba(58,89,77,.83)", transform: "translateY(1px)" },

  stars: {
    position: "absolute",
    zIndex: 4,
    top: 5,
    right: 8,
    width: 2.5,
    height: 2.5,
    borderRadius: "50%",
    background: "rgba(255,255,255,.96)",
    boxShadow:
      "8px 4px 0 -0.45px rgba(255,255,255,.88), 15px 11px 0 -0.65px rgba(255,255,255,.68), -7px 8px 0 -0.65px rgba(203,218,255,.62)",
    animation: "$starTwinkle 2.5s ease-in-out infinite",
    transition: "opacity .3s ease",
  },
  shooting: {
    position: "absolute",
    zIndex: 4,
    top: 6,
    right: 9,
    width: 13,
    height: 1,
    borderRadius: 999,
    transformOrigin: "right center",
    background: "linear-gradient(90deg,transparent,rgba(255,255,255,.92))",
    boxShadow: "0 0 5px rgba(255,255,255,.45)",
    animation: "$shootingStar 6s ease-in-out infinite 1.3s",
    transition: "opacity .25s ease",
  },

  cloud: {
    position: "absolute",
    zIndex: 4,
    left: 7,
    top: 7,
    width: 14,
    height: 5,
    borderRadius: 99,
    background: "rgba(255,255,255,.74)",
    boxShadow:
      "5px -3px 0 0 rgba(255,255,255,.60), 10px 1px 0 -1px rgba(255,255,255,.42)",
    animation: "$cloudDrift 4.5s ease-in-out infinite",
    transition: "opacity .3s ease",
  },

  sunRays: {
    position: "absolute",
    zIndex: 4,
    top: 2,
    right: 2,
    width: 26,
    height: 26,
    borderRadius: "50%",
    background:
      "repeating-conic-gradient(from 0deg,rgba(255,215,49,.82) 0deg 5deg,transparent 5deg 28deg)",
    opacity: .72,
    animation: "$sunBreathe 4.2s ease-in-out infinite",
    transition: "opacity .3s ease, transform .38s ease",
    WebkitMaskImage: "radial-gradient(circle,transparent 0 38%,#000 40% 100%)",
    maskImage: "radial-gradient(circle,transparent 0 38%,#000 40% 100%)",
  },

  orb: {
    position: "absolute",
    zIndex: 6,
    top: 4,
    width: 20,
    height: 20,
    borderRadius: "50%",
    transition:
      "left .42s cubic-bezier(.22,.9,.25,1.15), right .42s cubic-bezier(.22,.9,.25,1.15), background .3s ease, box-shadow .3s ease, transform .3s ease",
  },
  compactOrb: { top: 3, width: 19, height: 19 },
  sun: {
    right: 4,
    left: "auto",
    background: "linear-gradient(145deg,#ffe24d,#ffbd12)",
    boxShadow:
      "0 0 0 3px rgba(255,244,177,.22), 0 0 13px rgba(255,190,23,.58), inset -2px -2px 4px rgba(218,133,0,.18)",
  },
  moon: {
    left: 4,
    right: "auto",
    background: "linear-gradient(145deg,#eef3ff,#cfdcff)",
    boxShadow:
      "inset 7px -1px 0 #7589e8, 0 0 0 3px rgba(127,146,255,.16), 0 0 13px rgba(124,142,255,.42)",
  },

  sheen: {
    position: "absolute",
    zIndex: 8,
    inset: 1,
    pointerEvents: "none",
    borderRadius: 999,
    background: "linear-gradient(180deg,rgba(255,255,255,.16),transparent 48%)",
    boxShadow: "inset 0 -1px 0 rgba(0,0,0,.12)",
  },
}));

export default function ThemeToggle({ mode, onChange, compact = false, labels }) {
  const classes = useStyles();
  const isDark = mode === "dark";
  const copy = labels || { light: "Day mode", dark: "Dark mode" };
  const nextLabel = isDark ? copy.light : copy.dark;

  return (
    <button
      type="button"
      className={`${classes.button} ${compact ? classes.compact : ""}`}
      onClick={() => onChange(isDark ? "light" : "dark")}
      aria-label={nextLabel}
      title={nextLabel}
      aria-pressed={isDark}
    >
      <span
        className={`${classes.sky} ${isDark ? classes.darkSky : classes.lightSky}`}
        aria-hidden="true"
      />
      <span
        className={`${classes.atmosphere} ${isDark ? classes.nightAtmosphere : classes.dayAtmosphere}`}
        aria-hidden="true"
      />
      <span
        className={`${classes.ridgeBack} ${!isDark ? classes.dayRidgeBack : ""}`}
        aria-hidden="true"
      />
      <span
        className={`${classes.ridgeFront} ${!isDark ? classes.dayRidgeFront : ""}`}
        aria-hidden="true"
      />
      <span className={classes.stars} aria-hidden="true" style={{ opacity: isDark ? 1 : 0 }} />
      <span className={classes.shooting} aria-hidden="true" style={{ opacity: isDark ? 1 : 0 }} />
      <span className={classes.cloud} aria-hidden="true" style={{ opacity: isDark ? 0 : 1 }} />
      <span className={classes.sunRays} aria-hidden="true" style={{ opacity: isDark ? 0 : .72 }} />
      <span
        className={`${classes.orb} ${compact ? classes.compactOrb : ""} ${
          isDark ? classes.moon : classes.sun
        }`}
        aria-hidden="true"
      />
      <span className={classes.sheen} aria-hidden="true" />
    </button>
  );
}
